import i18n from '../i18n';

/**
 * Centralized user-facing messages in French.
 * Use these for consistent, user-friendly error handling.
 */

export const ErrorMessages = {
  // Authentication
  AUTH_INVALID_CREDENTIALS: "Email ou mot de passe incorrect",
  AUTH_REQUIRED: "Vous devez être connecté pour effectuer cette action",
  AUTH_SESSION_EXPIRED: "Votre session a expiré. Veuillez vous reconnecter.",

  // Organisation
  ORG_SELECTION_REQUIRED: "Veuillez sélectionner une organisation",
  ORG_LOAD_FAILED: "Impossible de charger les organisations. Vérifiez votre connexion et réessayez.",
  ORG_CREATE_FAILED: "Impossible de créer l'organisation. Vérifiez vos informations et réessayez.",
  ORG_UPDATE_FAILED: "Impossible de mettre à jour l'organisation. Réessayez.",
  ORG_DELETE_FAILED: "Impossible de supprimer l'organisation. Réessayez.",
  ORG_NOT_FOUND: "Organisation introuvable",
  ORG_USER_ALREADY_MEMBER: "Cet utilisateur appartient déjà à une organisation",
  ORG_USER_IS_OWNER: "Cet utilisateur est propriétaire d'une autre organisation et ne peut être invité",
  ORG_CANNOT_REMOVE_OWNER: "Le propriétaire de l'organisation ne peut pas être retiré",
  ORG_ASSET_TRANSFER_FAILED: "Erreur lors du transfert des assets. Réessayez.",

  // Project
  PROJECT_NAME_REQUIRED: "Le nom du projet est requis",
  PROJECT_INSTANCE_TYPE_REQUIRED: "Veuillez sélectionner un type d'instance",
  PROJECT_RESOURCES_REQUIRED: "Veuillez définir CPU et RAM",
  PROJECT_LOAD_FAILED: "Impossible de charger vos projets. Actualisez la page ou réessayez.",
  PROJECT_CREATE_FAILED: "La création du projet a échoué. Vérifiez vos quotas disponibles.",
  PROJECT_UPDATE_FAILED: "Impossible de mettre à jour le projet. Réessayez.",
  PROJECT_DELETE_FAILED: "Impossible de supprimer le projet. Réessayez.",
  PROJECT_NOT_FOUND: "Projet introuvable",

  // Deployment
  DEPLOYMENT_LOAD_FAILED: "Impossible de charger les déploiements. Réessayez.",
  DEPLOYMENT_CREATE_FAILED: "Le déploiement a échoué. Vérifiez vos configurations.",
  DEPLOYMENT_UPDATE_FAILED: "Impossible de mettre à jour le déploiement. Réessayez.",
  DEPLOYMENT_DELETE_FAILED: "Impossible de supprimer le déploiement. Réessayez.",
  DEPLOYMENT_QUOTA_EXCEEDED: "Quota insuffisant. Augmentez votre plan ou libérez des ressources.",

  // Repository
  REPOSITORY_CONNECTION_FAILED: "La connexion au repository Git a échoué. Vérifiez vos identifiants.",
  REPOSITORY_AUTH_FAILED: "Erreur d'autorisation. Vérifiez vos permissions.",
  REPOSITORY_TIMEOUT: "Délai d'autorisation dépassé. Réessayez.",

  // Backups
  BACKUP_LOAD_FAILED: "Impossible de charger les backups. Réessayez.",
  BACKUP_CREATE_FAILED: "Impossible de créer le backup. Réessayez.",
  BACKUP_RESTORE_FAILED: "Impossible de restaurer le backup. Vérifiez que le backup est valide.",
  BACKUP_DELETE_FAILED: "Impossible de supprimer le backup. Réessayez.",

  // Invitations
  INVITATION_SEND_FAILED: "Impossible d'envoyer l'invitation. Vérifiez l'email et réessayez.",
  INVITATION_ACCEPT_FAILED: "Impossible d'accepter l'invitation. Réessayez.",
  INVITATION_DECLINE_FAILED: "Impossible de refuser l'invitation. Réessayez.",

  // Generic
  GENERIC_ERROR: "Une erreur s'est produite. Veuillez réessayer.",
  NETWORK_ERROR: "Erreur de connexion. Vérifiez votre connexion Internet et réessayez.",
  VALIDATION_ERROR: "Les informations saisies sont invalides. Vérifiez vos données.",
  QUOTA_EXCEEDED: "Quota insuffisant. Augmentez votre plan ou libérez des ressources.",
  PERMISSION_DENIED: "Vous n'avez pas les permissions nécessaires pour cette action",
  RESOURCE_NOT_FOUND: "Ressource introuvable",
  ENV_HAS_DEPLOYMENTS: "Impossible de supprimer cet environnement car il contient encore des applications déployées. Veuillez d'abord supprimer toutes les applications.",
  PROJECT_HAS_ENVIRONMENTS: "Impossible de supprimer ce projet car il contient encore des environnements. Veuillez d'abord supprimer tous les environnements.",
} as const;

// Messages de confirmation pour les actions destructives
export const ConfirmationMessages = {
  DELETE_PROJECT_TITLE: "Supprimer le projet",
  DELETE_PROJECT_MESSAGE: "Cette action est irréversible. Toutes les données associées seront perdues.",
  DELETE_PROJECT_WARNING: "Assurez-vous d'avoir supprimé tous les environnements avant de continuer.",

  DELETE_ENVIRONMENT_TITLE: "Supprimer l'environnement",
  DELETE_ENVIRONMENT_MESSAGE: "Cette action est irréversible. Toutes les applications et configurations seront perdues.",
  DELETE_ENVIRONMENT_WARNING: "Assurez-vous d'avoir supprimé toutes les applications déployées avant de continuer.",

  DELETE_DEPLOYMENT_TITLE: "Supprimer l'application",
  DELETE_DEPLOYMENT_MESSAGE: "Cette action supprimera définitivement l'application et toutes ses données.",
  DELETE_DEPLOYMENT_WARNING: "Les backups seront conservés si vous avez activé cette fonctionnalité.",

  DELETE_ACCOUNT_TITLE: "Supprimer votre compte",
  DELETE_ACCOUNT_MESSAGE: "Cette action est irréversible. Toutes vos données seront définitivement supprimées.",
  DELETE_ACCOUNT_WARNING: "Vous devez d'abord supprimer tous vos projets, environnements et applications.",

  CONFIRM_BUTTON: "Oui, supprimer",
  CANCEL_BUTTON: "Annuler",
} as const;

export const SuccessMessages = {
  // Authentication
  LOGIN_SUCCESS: "Connexion réussie !",
  REGISTRATION_SUCCESS: "Inscription réussie ! Vérifiez votre email pour activer votre compte.",
  LOGOUT_SUCCESS: "Déconnexion réussie",
  PASSWORD_RESET_SUCCESS: "Mot de passe réinitialisé avec succès",

  // Organisation
  ORG_CREATED: "Organisation créée avec succès !",
  ORG_UPDATED: "Organisation mise à jour avec succès",
  ORG_DELETED: "Organisation supprimée avec succès",
  ORG_MEMBER_ADDED: "Membre ajouté avec succès",
  ORG_MEMBER_REMOVED: "Membre retiré avec succès",
  ORG_MEMBER_REMOVED_WITH_TRANSFER: "Membre retiré et assets transférés avec succès",
  ORG_ACCOUNT_DELETED_WITH_TRANSFER: "Compte supprimé et assets transférés avec succès",

  // Project
  PROJECT_CREATED: "Projet créé avec succès !",
  PROJECT_UPDATED: "Projet mis à jour avec succès",
  PROJECT_DELETED: "Projet supprimé avec succès",

  // Deployment
  DEPLOYMENT_CREATED: "Déploiement lancé avec succès",
  DEPLOYMENT_UPDATED: "Déploiement mis à jour avec succès",
  DEPLOYMENT_DELETED: "Déploiement supprimé avec succès",
  DEPLOYMENT_STOPPED: "Déploiement arrêté avec succès",
  DEPLOYMENT_RESTARTED: "Déploiement redémarré avec succès",

  // Repository
  REPOSITORY_CONNECTED: "Repository connecté avec succès !",

  // Backups
  BACKUP_CREATED: "Backup créé avec succès !",
  BACKUP_RESTORED: "Backup restauré avec succès",
  BACKUP_DELETED: "Backup supprimé avec succès",

  // Invitations
  INVITATION_SENT: "Invitation envoyée avec succès",
  INVITATION_ACCEPTED: "Invitation acceptée avec succès",
  INVITATION_DECLINED: "Invitation refusée",
} as const;

/**
 * Type for API error response structure
 */
interface ApiErrorDetail {
  error?: string;
  message?: string;
  quota_exceeded?: boolean;
}

/**
 * Une entree du `detail` d'un 422 FastAPI (erreur de validation Pydantic).
 * Format renvoye par l'API : {"detail": [{loc, msg, type, input, ctx}, ...]}
 * cf. le handler `validation_exception_handler` de nubicloud-api (main.py).
 */
export interface ApiValidationError {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
  input?: unknown;
}

interface ApiErrorResponse {
  response?: {
    status?: number;
    data?: {
      // Sur un 422, `detail` est un ARRAY — d'ou la branche dediee dans
      // getApiErrorMessage. Le typer ici evite de re-oublier le cas.
      detail?: string | ApiErrorDetail | ApiValidationError[];
    };
  };
  code?: string;
  message?: string;
}

/** Prefixes de source FastAPI dans `loc`, sans interet pour l'utilisateur. */
const LOC_SOURCE_PREFIXES = ['body', 'query', 'path', 'header', 'cookie'];

/** Au-dela, le toast devient illisible : on tronque et on annonce le reste. */
const MAX_VALIDATION_ERRORS_SHOWN = 3;

/**
 * Nom de champ lisible depuis le `loc` FastAPI.
 * ['body', 'additional_databases', 0, 'name'] -> 'additional_databases.name'
 * On retire le prefixe de source et les index de liste (l'utilisateur ne
 * raisonne pas en indices).
 */
function fieldLabelFromLoc(loc?: (string | number)[]): string | null {
  if (!Array.isArray(loc) || loc.length === 0) return null;
  const parts = loc
    .filter((part, i) => !(i === 0 && typeof part === 'string' && LOC_SOURCE_PREFIXES.includes(part)))
    .filter((part): part is string => typeof part === 'string');
  return parts.length > 0 ? parts.join('.') : null;
}

/**
 * Transforme le `detail` d'un 422 FastAPI en message affichable.
 *
 * Point cle : on remonte le NOM DU CHAMP en plus du message, sinon sur un
 * formulaire a N champs l'utilisateur sait ce qui ne va pas mais pas ou.
 * Les champs qui partagent le meme message sont regroupes (un meme validator
 * applique a `name` et `user` produit deux fois la meme phrase).
 *
 * @returns le message formate, ou null si `detail` n'est pas exploitable
 *          (array vide, entrees sans `msg`) — le caller enchaine alors sur
 *          son fallback habituel.
 */
export function formatValidationErrors(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;

  // msg -> liste des champs concernes (Map = ordre d'insertion preserve)
  const byMessage = new Map<string, string[]>();

  for (const entry of detail as ApiValidationError[]) {
    if (!entry || typeof entry !== 'object' || typeof entry.msg !== 'string') continue;
    // Pydantic v2 prefixe les ValueError custom par "Value error, " — bruit pur
    // pour l'utilisateur, le message metier commence apres.
    const msg = entry.msg.replace(/^Value error,\s*/i, '').trim();
    if (!msg) continue;

    const field = fieldLabelFromLoc(entry.loc);
    const fields = byMessage.get(msg) ?? [];
    if (field && !fields.includes(field)) fields.push(field);
    byMessage.set(msg, fields);
  }

  if (byMessage.size === 0) return null;

  const entries = Array.from(byMessage.entries());
  const shown = entries.slice(0, MAX_VALIDATION_ERRORS_SHOWN).map(([msg, fields]) =>
    fields.length > 0
      ? i18n.t('error.VALIDATION_FIELD', { ns: 'messages', field: fields.join(', '), message: msg })
      : msg
  );

  const hidden = entries.length - shown.length;
  if (hidden > 0) {
    shown.push(i18n.t('error.VALIDATION_MORE', { ns: 'messages', count: hidden }));
  }

  return shown.join(' · ');
}

/**
 * Check if error is a network/connection error
 */
function isNetworkError(error: unknown): boolean {
  const apiError = error as ApiErrorResponse;

  // Axios network errors have no response and specific codes
  if (!apiError.response) {
    const code = apiError.code;
    if (code === 'ERR_NETWORK' || code === 'ECONNREFUSED' || code === 'ENOTFOUND') {
      return true;
    }
    // Check error message for network-related keywords
    const message = apiError.message?.toLowerCase() || '';
    if (message.includes('network') || message.includes('connection')) {
      return true;
    }
  }
  return false;
}

/**
 * Check if error is a timeout error
 */
function isTimeoutError(error: unknown): boolean {
  const apiError = error as ApiErrorResponse;

  // Axios timeout errors
  if (apiError.code === 'ECONNABORTED' || apiError.code === 'ETIMEDOUT') {
    return true;
  }

  // Check message for timeout keywords
  const message = apiError.message?.toLowerCase() || '';
  if (message.includes('timeout') || message.includes('timed out')) {
    return true;
  }

  return false;
}

/**
 * Helper function to extract user-friendly error message from API errors
 * Handles:
 * - Axios errors with response.data.detail
 * - Network errors (no internet, server unreachable)
 * - Timeout errors
 * - Generic JavaScript errors
 *
 * @param error - Error object (typically from axios)
 * @param fallbackMessage - Optional fallback message if extraction fails
 * @returns User-friendly error message in French
 */
export function getApiErrorMessage(error: unknown, fallbackMessage?: string): string {
  // Handle null/undefined
  if (!error) {
    return fallbackMessage || ErrorMessages.GENERIC_ERROR;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Type guard for error with response
  const apiError = error as ApiErrorResponse;

  // Check for timeout errors first
  if (isTimeoutError(error)) {
    return i18n.t('error.TIMEOUT', { ns: 'messages' });
  }

  // Check for network errors
  if (isNetworkError(error)) {
    return ErrorMessages.NETWORK_ERROR;
  }

  // Check if error has no response (network issue or other)
  if (!apiError.response) {
    // Check if it's a standard Error object
    if (error instanceof Error) {
      // Don't expose raw error messages to users in production
      // but allow them in development for debugging
      if (process.env.NODE_ENV === 'development') {
        return error.message || fallbackMessage || ErrorMessages.GENERIC_ERROR;
      }
    }
    return fallbackMessage || ErrorMessages.NETWORK_ERROR;
  }

  // Extract detail from API response
  const apiDetail = apiError.response?.data?.detail;

  // If API provides a French message, use it
  if (apiDetail && typeof apiDetail === 'string') {
    return apiDetail;
  }

  // 422 FastAPI : `detail` est un array d'erreurs de validation Pydantic.
  // CE TEST DOIT RESTER AVANT celui sur `typeof === 'object'` : un array passe
  // ce test-la, mais n'a ni `.error` ni `.message` — on retombait alors dans le
  // switch(status) sans case 422, donc sur GENERIC_ERROR, et le message precis
  // de l'API (ex. "Nom invalide (tirets...) Exemple: savana_money") etait perdu.
  if (Array.isArray(apiDetail)) {
    const validationMessage = formatValidationErrors(apiDetail);
    if (validationMessage) return validationMessage;
  }

  // If detail is an object with error or message field
  if (apiDetail && !Array.isArray(apiDetail) && typeof apiDetail === 'object') {
    if (apiDetail.error) return apiDetail.error;
    if (apiDetail.message) return apiDetail.message;
  }

  // Map common HTTP status codes to user-friendly messages
  const status = apiError.response?.status;
  switch (status) {
    case 400:
      return fallbackMessage || ErrorMessages.VALIDATION_ERROR;
    case 401:
      return ErrorMessages.AUTH_INVALID_CREDENTIALS;
    case 403:
      return ErrorMessages.PERMISSION_DENIED;
    case 404:
      return ErrorMessages.RESOURCE_NOT_FOUND;
    case 408:
      return i18n.t('error.TIMEOUT', { ns: 'messages' });
    case 422:
      // Filet de securite : 422 sans `detail` exploitable (array vide, entrees
      // sans `msg`). Le cas nominal est traite plus haut.
      return fallbackMessage || ErrorMessages.VALIDATION_ERROR;
    case 409:
      return i18n.t('error.CONFLICT', { ns: 'messages' });
    case 429:
      return i18n.t('error.RATE_LIMITED', { ns: 'messages' });
    case 500:
      return i18n.t('error.SERVER_ERROR', { ns: 'messages' });
    case 502:
      return i18n.t('error.SERVICE_UNAVAILABLE', { ns: 'messages' });
    case 503:
      return i18n.t('error.SERVICE_UNAVAILABLE', { ns: 'messages' });
    case 504:
      return i18n.t('error.GATEWAY_TIMEOUT', { ns: 'messages' });
    default:
      return fallbackMessage || ErrorMessages.GENERIC_ERROR;
  }
}

/**
 * Helper function to check if error is a quota exceeded error
 * @param error - Error object (typically from axios)
 * @returns true if error is quota related
 */
export function isQuotaError(error: unknown): boolean {
  const apiError = error as ApiErrorResponse;
  const detail = apiError.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail.toLowerCase().includes('quota');
  }
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail) && detail.quota_exceeded) {
    return true;
  }
  return false;
}
